/**
 * Copyright © Amazon.com and Affiliates: This deliverable is considered Developed Content as defined in the AWS Service Terms 
 * and the SOW between the parties dated 10/31/2024.
 */
import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { USER_ID_DEV } from '../../../mock/user';
import { INPUT_DATE_RANGES, P_AS_OF_DATE_OPTIONS } from '../../../constants';
import { TokenPropertyService } from '../token-property.service';

export type Version = {
  name: string,
  creationTime: number,
  downloadLink: string
}

export type Subscription = {
  dashboardId: string,
  userId: string,
  subscriptionId: string,
  status: string,
  cronSchedule: string,
  description: string,
  sheetId: string,
  format: string,
  endDttm: string,
  storageURL: string,
  title: string,
  visualId?: string,
  parameters?: object,
  subscriptionName?: string,     // Add the subscription name field
  subscriptionStartDate?: string, // Add the start date field
  subscriptionEndDate?: string,    // Add the end date field
  contentid?: string,
}

export type Report = {
  userId: string,
  subscriptionId: string,
  status: string,
  runId: string,
  storageURL: string,
  startDttm: string,
  lastUpdateDttm: string,
  endDttm: string,
  format: string,
  title: string,
  version?: string;
}

export type S3Report = {
  userId: string,
  subscriptionId: string,
  status: string,
  runId: string,
  storageURL: string,
  startDttm: string,
  lastUpdateDttm: string,
  endDttm: string,
  format: string,
  title: string,
  version?: string;
  jobStatus?: string;
  displayMessage?: string;
}

export type Setting = {
  emailId: string,
  notificationInd: any;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionsService {
  private selectedSubscriptionSource = new BehaviorSubject<Subscription | null>(null);
  private API_URL: string;
  selectedSubscription$ = this.selectedSubscriptionSource.asObservable();
  private token: any;
  private tokenDetails: any;
  private userId: any = USER_ID_DEV;
  private idToken: any;


  constructor(
    private http: HttpClient,
    @Inject('API_URL') apiUrl: string,
    private tokenPropertyService: TokenPropertyService
  ) {
    this.API_URL = apiUrl;
    this.token = localStorage.getItem('token');
    this.idToken = localStorage.getItem('id_token');
    this.userId = this.tokenPropertyService.getPropertyFromToken('sub', 'token') || USER_ID_DEV;
  }

  getUserSubscriptions(): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `${this.token}`).set('id_token', `${this.idToken}`);
    return this.http.get<any>(`${this.API_URL}/user/subscriptions`, { headers })
      .pipe(
        map(response => response.Items)
      );
  }

  createUserSubscription(body: any) {
    const headers = new HttpHeaders().set('Authorization', `${this.token}`).set('id_token', `${this.idToken}`);
    return this.http.post<any>(`${this.API_URL}/user/subscriptions`, body, { headers })
  }

  setSelectedSubscription(subscription: Subscription | null) {
    this.selectedSubscriptionSource.next(subscription);
  }

  getSubscriptionReports(subscriptionId: string): Observable<S3Report[]> {
    const headers = new HttpHeaders().set('Authorization', `${this.token}`).set('id_token', `${this.idToken}`);
    return this.http.get<any>(`${this.API_URL}/user/s3Reports?userId=${this.userId}&subscriptionId=${subscriptionId}`, { headers })
      .pipe(
        map(response => response.return_list)
      );
  }

  getUserGuide(): Observable<Record<string, string>> {
    const headers = new HttpHeaders().set('Authorization', `${this.token}`).set('id_token', `${this.idToken}`);
    return this.http.get<Record<string, string>>(`${this.API_URL}/user/guides`, { headers });
  }

  deleteSubscription(body: any) {
    const headers = new HttpHeaders().set('Authorization', `${this.token}`).set('id_token', `${this.idToken}`);
    return this.http.delete<any>(`${this.API_URL}/user/subscriptions`, { headers, body })
  }

  getDownloadLink(storageURL: string) {
    return `${this.API_URL}/user/s3-presigned?s3_url=${storageURL}`;
  }

  downloadFile(storageURL: string) {
    const headers = new HttpHeaders().set('Authorization', `${this.token}`).set('id_token', `${this.idToken}`);
    const completeStorageURL = `${this.API_URL}/user/s3-presigned?s3_url=${storageURL}`;

    // Get the presignedURL
    this.http.get<any>(completeStorageURL, { headers }).subscribe(response => {
      const presignedURL = response.presignedURL;

      const downloadLink = document.createElement('a');
      downloadLink.href = presignedURL;
      downloadLink.download = '';
      document.body.appendChild(downloadLink);
      downloadLink.click();

      // Cleanup
      document.body.removeChild(downloadLink);

    });
  }

  generateSubscriptionUriFromTitle(subscription: Subscription) {
    return subscription.title.replace(/\s+/g, '_').toLowerCase();
  }

  getUserSettings(): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `${this.token}`).set('id_token', `${this.idToken}`);
    return this.http.get<any>(`${this.API_URL}/user/settings?userId=${this.userId}`, { headers })
      .pipe(
        map(response => response.settings )
      );
  }

  createUpdateUserSetting(body: any) {
    const headers = new HttpHeaders().set('Authorization', `${this.token}`).set('id_token', `${this.idToken}`);
    return this.http.post<any>(`${this.API_URL}/user/settings`, body, { headers })
  }

  getParameterConfigs(dashboardId: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `${this.token}`).set('id_token', `${this.idToken}`);
    return this.http.get<any>(`${this.API_URL}/report-params?dashboardId=${dashboardId}`, { headers })
      .pipe(
        map(response => {
          return this.processParameterResponse(response);
        })
      );
  }

  private processParameterResponse(response: any) {
    try {
      // Defensive checks for response structure
      if (!response?.return_response?.parameters_from_definition) {
        console.error('💥 Invalid API response structure');
        return { parameters_from_definition: [] };
      }
      
      if (!Array.isArray(response.return_response.parameters_from_definition)) {
        console.error('💥 parameters_from_definition is not an array');
        return { parameters_from_definition: [] };
      }
      
          // Validate API contract consistency (simplified for production)
          const dynamoParams = Object.keys(response.return_response?.parameters_from_dynamo || {});
          const definitionParams = response.return_response.parameters_from_definition
            .map((element: any) => element.StringParameterDeclaration?.Name || element.DateTimeParameterDeclaration?.Name)
            .filter((name: any) => name);
          
          if (dynamoParams.length === 0 && definitionParams.length > 0) {
            console.warn('⚠️ API Response missing parameters_from_dynamo field - dropdowns will be empty');
          }
          
          response.return_response.parameters_from_definition.forEach((element: {
          label: string; options: any; 
          StringParameterDeclaration?: { 
            Name: any; 
            DefaultValues?: { 
              DynamicValue?: { 
                DefaultValueColumn?: { 
                  ColumnName?: string | number; 
                }; 
              }; 
            }; 
          };
          DateTimeParameterDeclaration?: { Name: any; };
          DecimalParameterDeclaration?: { Name: any; };
          IntegerParameterDeclaration?: { Name: any; };
        }, index: number) => {
            try {
              // Get parameter name from any parameter type
              const paramName = element.StringParameterDeclaration?.Name || 
                               element.DateTimeParameterDeclaration?.Name || 
                               element.DecimalParameterDeclaration?.Name ||
                               element.IntegerParameterDeclaration?.Name;

              // Set display label from parameter_display_names or use parameter name as fallback
              if (paramName) {
                element.label = response.return_response.parameter_display_names?.[paramName] || paramName;
              }

            // Handle options assignment for StringParameterDeclaration and DecimalParameterDeclaration
            if (element.StringParameterDeclaration || element.DecimalParameterDeclaration) {
              // Check if we have parameters_from_dynamo in the response
              if (response.return_response.parameters_from_dynamo) {
                // Handle the new API structure with direct parameter mappings
                if (paramName && response.return_response.parameters_from_dynamo[paramName]) {
                  // Use the parameter options directly from parameters_from_dynamo
                  element.options = response.return_response.parameters_from_dynamo[paramName].sort((a: string, b: string) => a > b ? 1 : -1);
                } else {
                  // Fallback to the old logic for dynamic column lookup
                  const columnName = element.StringParameterDeclaration?.DefaultValues?.DynamicValue?.DefaultValueColumn?.ColumnName;
                  if (columnName && response.return_response.parameters_from_dynamo[columnName]) {
                    element.options = response.return_response.parameters_from_dynamo[columnName].sort((a: string, b: string) => a > b ? 1 : -1);
                  } else {
                    element.options = [];
                    console.warn(`⚠️ No options available for parameter '${paramName}' - will render as empty dropdown`);
                  }
                }
              } else {
                // PROD Fix: Handle missing parameters_from_dynamo gracefully
                element.options = [];
                console.warn(`⚠️ parameters_from_dynamo missing from API - parameter '${paramName}' will have empty dropdown`);
              }
            }
            
            // For DateTimeParameterDeclaration, set empty options array (will be handled separately below)
            if (element.DateTimeParameterDeclaration) {
              element.options = [];
            }
            
            } catch (paramError) {
              console.error('💥 Error processing parameter:', element, paramError);
              element.options = []; // Fallback to empty array
            }
          })
          
          // Handle pStartDate/pEndDate OR pBeginDate/pEndDate conversion to pDateRange
          const hasStartDate = response.return_response.parameters_from_definition.some((element: {
            DateTimeParameterDeclaration: any; StringParameterDeclaration: { Name: string; };
          }) => element.DateTimeParameterDeclaration?.Name?.toLowerCase() === 'pstartdate');
          
          const hasBeginDate = response.return_response.parameters_from_definition.some((element: {
            DateTimeParameterDeclaration: any; StringParameterDeclaration: { Name: string; };
          }) => element.DateTimeParameterDeclaration?.Name?.toLowerCase() === 'pbegindate');
          
          const hasEndDate = response.return_response.parameters_from_definition.some((element: {
            DateTimeParameterDeclaration: any; StringParameterDeclaration: { Name: string; };
          }) => element.DateTimeParameterDeclaration?.Name?.toLowerCase() === 'penddate');

          if ((hasStartDate || hasBeginDate) && hasEndDate) {
            const newInput = {
              label: 'Date Range',
              options: INPUT_DATE_RANGES,
              StringParameterDeclaration: {
                Name: 'pDateRange',
                ParameterValueType:
                  "SINGLE_VALUED"
              }
            }
            response.return_response.parameters_from_definition = response.return_response.parameters_from_definition.filter((element: {
              DateTimeParameterDeclaration: any; StringParameterDeclaration: { Name: string; };
            }) => {
              const name = element.DateTimeParameterDeclaration?.Name?.toLowerCase();
              return name !== 'pstartdate' && name !== 'pbegindate' && name !== 'penddate';
            })
            response.return_response.parameters_from_definition.push(newInput)
          }
          
          // Handle pAsOfDate conversion to dropdown (case-insensitive)
          const asOfDateElement = response.return_response.parameters_from_definition.find((element: {
            DateTimeParameterDeclaration: any; StringParameterDeclaration: { Name: string; };
          }) => element.DateTimeParameterDeclaration?.Name?.toLowerCase() === 'pasofdate');
          
          if (asOfDateElement) {
            const originalName = asOfDateElement.DateTimeParameterDeclaration.Name;
            const asOfDateInput = {
              label: response.return_response.parameter_display_names[originalName] || 'As Of Date',
              options: P_AS_OF_DATE_OPTIONS,
              StringParameterDeclaration: {
                Name: originalName,
                ParameterValueType: "SINGLE_VALUED"
              }
            }
            // Remove the original DateTimeParameterDeclaration for pAsOfDate
            response.return_response.parameters_from_definition = response.return_response.parameters_from_definition.filter((element: {
              DateTimeParameterDeclaration: any; StringParameterDeclaration: { Name: string; };
            }) => element.DateTimeParameterDeclaration?.Name?.toLowerCase() !== 'pasofdate')
            // Add the new StringParameterDeclaration for pAsOfDate
            response.return_response.parameters_from_definition.push(asOfDateInput)
          }
          
          return response.return_response;
    } catch (error) {
      console.error('💥 Critical error in processParameterResponse:', error);
      console.error('📊 Error context - response:', response);
      // Return empty parameters to prevent complete failure
      return { parameters_from_definition: [] };
    }
  }
}
